import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import DataComponentBody from './DataComponent__Body';
import DataComponentFilter from './DataComponent__Filter';
import DataComponentDisplayControl from './DataComponent__DisplayControl';
import DateComponentSettings from './DateComponent_Settings';
import DateComponentCurrencySettings from './DateComponent_CurrencySettings';
import Pagination from '../Pagination.js';
import query from '../../helpers/query';
import {isMobile} from "utils/DeviceDetect";

const DataComponent = ({
    fields = [],
    autoTile = false,
    onAction,
    onActionFilter,
    isQuery = false,
    page: propPage,
    totalPages,
    loading = false,
    alignPagination = false,
    settings = false,
    className = '',
    settingKey = '',
    columnData = { header: {}, body: {} },
    displayControl,
    headerNames,
    headerNamesArray = false,
    multiLevelHeaderNames,
    tableBodyTitle,
    sessionId,
    totalRows,
    data,
    currencySettings,
    currencySettingKey,
    currencySettingData,
    hideResetButton,
    gray
}) => {
    const [filters, setFilters] = useState({});
    const [typeDisplayControl, setTypeDisplayControl] = useState(() => {
        return autoTile && isMobile ? 'tile' : 'table';
    });
    const [page, setPage] = useState(propPage);

    useEffect(() => {
        setPage(propPage);
    }, [propPage]);

    useEffect(() => {
        if (!fields.length && onAction) {
            onAction(filters, 'firstRender');
        }
    }, [fields.length, onAction, filters]);

    const handleAction = useCallback((type) => {
        const state = {
            ...filters
        };

        if (onAction) {
            onAction(state, type);
        }

        if (isQuery) {
            query.replace(state);
        }
    }, [filters, onAction, isQuery]);

    const handleActionFilter = useCallback((type) => {
        const state = {
            ...filters
        };

        if (onActionFilter) {
            onActionFilter(state, type);
        }
    }, [filters, onActionFilter]);

    const onPageChange = useCallback((newPage) => {
        const newFilters = {
            ...filters,
            page: newPage
        };
        setFilters(newFilters);
        handleAction('pageChange');
    }, [filters, handleAction]);

    const onFiltersApply = useCallback((state, type = 'filtersChange') => {
        setFilters(state);
        if (typeof onActionFilter !== "undefined") {
            handleActionFilter(type);
        } else {
            handleAction(type);
        }
    }, [onActionFilter, handleAction, handleActionFilter]);

    const onDisplayControlChange = useCallback((type) => {
        setTypeDisplayControl(type);
    }, []);

    const getClassFooter = useCallback(() => {
        return classNames({
            'ibox-footer': true,
            'text-right': alignPagination
        });
    }, [alignPagination]);

    return (
        <div className="ibox">
            {fields.length
                ? (
                    <DataComponentFilter
                        fields={fields}
                        loading={loading}
                        onAction={onFiltersApply}
                        hideResetButton={hideResetButton}
                        gray={gray}
                    />
                )
                : null
            }
            <DateComponentSettings 
                settings={settings}
                headerNames={headerNames}
                tableBodyTitle={tableBodyTitle}
                settingKey={settingKey}
                loading={loading}
                sessionId={sessionId}
                totalRows={totalRows}
                data={data}
                filters={filters}
            />
            <DateComponentCurrencySettings
                settings={currencySettings}
                headerNames={headerNames}
                headerNamesArray={headerNamesArray}
                autoTile={autoTile}
                tableBodyTitle={tableBodyTitle}
                settingKey={currencySettingKey}
                loading={loading}
                data={currencySettingData}
                filters={filters}
            />
            <div className="ibox-content">
                {displayControl
                    ? <DataComponentDisplayControl
                        data={displayControl.data}
                        type={typeDisplayControl}
                        name={displayControl.name}
                        onChange={onDisplayControlChange}
                      />
                    : null
                }
                <DataComponentBody
                    data={data}
                    headerNames={headerNames}
                    multiLevelHeaderNames={multiLevelHeaderNames}
                    columnData={columnData}
                    loading={loading}
                    settings={settings}
                    settingKey={settingKey}
                    displayControl={{
                        ...displayControl,
                        type: typeDisplayControl
                    }}
                    filters={filters}
                    className={className}
                />
            </div>
            {totalPages > 1
                ? <div className={getClassFooter()}>
                    <Pagination
                        onPageChange={onPageChange}
                        initialPage={filters.page}
                        totalPages={totalPages}
                        loading={loading}
                        align={alignPagination ? 'right' : 'left'}
                    />
                  </div>
                : null
            }
        </div>
    );
};

DataComponent.propTypes = {
    isQuery: PropTypes.bool,
    autoTile: PropTypes.bool,
    headerNamesArray: PropTypes.bool,
    loading: PropTypes.bool,
    page: PropTypes.number,
    alignPagination: PropTypes.bool,
    settings: PropTypes.bool,
    fields: PropTypes.array,
    className: PropTypes.string,
    settingKey: PropTypes.string,
    columnData: PropTypes.shape({
        header: PropTypes.object,
        body: PropTypes.object
    }),
    displayControl: PropTypes.shape({
        type: PropTypes.string,
        name: PropTypes.string,
        data: PropTypes.array
    }),
    onAction: PropTypes.func,
    onActionFilter: PropTypes.func,
    totalPages: PropTypes.number,
    headerNames: PropTypes.object,
    tableBodyTitle: PropTypes.string,
    sessionId: PropTypes.string,
    totalRows: PropTypes.number,
    data: PropTypes.array,
    currencySettings: PropTypes.bool,
    currencySettingKey: PropTypes.string,
    currencySettingData: PropTypes.array,
    hideResetButton: PropTypes.bool,
    gray: PropTypes.bool,
    multiLevelHeaderNames: PropTypes.object
};

export default DataComponent;
